import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_COLLECTION } from '@/modules/collection/router';
import { CollectionSelectOptions, CollectionType } from '@/modules/collection/types';
import { ROUTE_NAME_HOMEPAGE } from '@/modules/main/router';
import { collectionService } from '@/services/collection';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { onMounted, reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useCollectionAddOrEdit() {
    const router = useAppRouter();
    const route = useRoute();
    const { executeAction, excuteAndNotify } = useCrudActions();

    const model = reactive({
        collectionId: StringUtils.toNumber(route.params.collectionId),
        type: StringUtils.toNumber(route.query.type),
        name: '',
        coverColor: null,
        image: null,
        imageFile: null,

        get isUpdate() {
            return model.collectionId != null;
        },
        get isCoverColorRequired() {
            return model.type != CollectionType.Journey;
        },
        get pageLabel() {
            return model.isUpdate ? 'Edit Collection' : 'Create Collection';
        },
        get buttonLabel() {
            return model.isUpdate ? 'Update' : 'Create';
        },
        get canSelectType() {
            return !model.isUpdate && model.type != CollectionType.Journey;
        },
        get selectableTypeOptions() {
            if (model.type == CollectionType.Journey) {
                return CollectionSelectOptions.Type.filter(type => type.value == CollectionType.Journey);
            }

            return CollectionSelectOptions.Type.filter(type => type.value !== CollectionType.Journey);
        }
    });

    const validations = {
        name: { required },
        type: { required },
        coverColor: { requiredIf: requiredIf(() => model.isCoverColorRequired) }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = executeAction({
        request: () => collectionService.get(model.collectionId),
        onSuccess: ({ data }) => Object.assign(model, data),
        onError: () => router.push({ name: ROUTE_NAME_COLLECTION.INDEX(model.type) })
    });

    const createOrUpdate = excuteAndNotify({
        validator: v$,
        request: () =>
            model.isUpdate
                ? collectionService.update(
                      model.collectionId,
                      model.name,
                      model.type,
                      model.coverColor,
                      model.imageFile
                  )
                : collectionService.create(model.name, model.type, model.coverColor, model.imageFile),
        onSuccess: ({ data }) => {
            const id = model.isUpdate ? model.collectionId : data;
            router.push({ name: ROUTE_NAME_COLLECTION.DETAIL(model.type), params: { collectionId: id } });
        }
    });

    onMounted(async () => {
        if (model.isUpdate) {
            await fetch();
        } else if (model.type == null) {
            router.push({ name: ROUTE_NAME_HOMEPAGE });
        }
    });

    return { model, v$, createOrUpdate };
}
